import React, { FC, useState, useEffect, useRef } from "react";
import { Table, Button, Input, Select, Space, notification, Modal, Descriptions, Dropdown, Pagination, Drawer } from "antd";
import {
  InfoCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import rocksLeft from '../assets/images/background/RocksWithBird.png';
import rocksRight from '../assets/images/background/RocksWithGrass2.png';
// Assets
import skyBg from '../assets/images/background/SkyWithClouds.png';
import skyBgMobile from '../assets/images/background/SkyWithCloudsMobile.png';
import FileText from '../components/svg/FileText';
import ArrowRight from "../components/svg/ArrowRight";
import ArrowRightWhite from "../components/svg/ArrowRightWhite";
import EyeIcon from "../components/svg/EyeIcon";

// Components
import Header from "../components/Header";

// Utils
import { fullname, role, email } from '../utils/data';
import { getAuthToken, getCases } from '../utils/functions';
import { CaseDetailsUrl } from '../utils/network';
import { CaseRecordProps } from '../utils/types';


const { Option } = Select;

const CaseManagement: FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCaseType, setSelectedCaseType] = useState<string>('all');
  const [allCases, setAllCases] = useState<CaseRecordProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseRecordProps | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<CaseRecordProps | null>(null);
  const [deleting, setDeleting] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ left: 0, width: 0, client: 0 });

  // User info
  const currentUser = {
    role: localStorage.getItem(role) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(email) || ''
  };
  const isAdmin = currentUser.role.toLowerCase() === 'admin';

  // Fetch cases
  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = () => {
    getCases(setAllCases, setLoading);
  };

  const handleConfirmDelete = async () => {
    if (!caseToDelete) return;
    setDeleting(true);
    try {
      const headers = getAuthToken();
      if (!headers) throw new Error('Authentication required');
      await axios.delete(`${CaseDetailsUrl}${caseToDelete.id}/`, headers);
      notification.success({
        message: 'Interview Deleted',
        description: `Case #${caseToDelete.case_no} has been deleted successfully.`
      });
      setDeleteModalVisible(false);
      setCaseToDelete(null);
      fetchCases();
    } catch (error) {
      notification.error({
        message: 'Delete Failed',
        description: 'Failed to delete the interview.'
      });
    } finally {
      setDeleting(false);
    }
  };

  const filteredCases = React.useMemo(() => allCases.filter(case_ => {
    const complainantName = [case_.first_name, case_.middle, case_.last_name].filter(Boolean).join(' ');
    const caseType = case_.case_type || case_.type || '';
    const matchesSearch = searchTerm === '' ||
      case_.case_no?.toString().includes(searchTerm) ||
      complainantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedCaseType === 'all' || caseType === selectedCaseType;
    return matchesSearch && matchesType;
  }), [allCases, searchTerm, selectedCaseType]);

  const pagedData = React.useMemo(() => filteredCases.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ), [filteredCases, currentPage, pageSize]);

  // Auto-adjust page if deletion or filtering makes current page empty
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredCases.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredCases.length, currentPage, pageSize]);

  // Custom Scrollbar Sync Logic
  useEffect(() => {
    const tableBody = tableContainerRef.current?.querySelector('.ant-table-content');
    if (!tableBody) return;

    const onScroll = () => {
      setScrollInfo({
        left: tableBody.scrollLeft,
        width: tableBody.scrollWidth,
        client: tableBody.clientWidth
      });
    };

    tableBody.addEventListener('scroll', onScroll);
    onScroll(); // Initial sync

    window.addEventListener('resize', onScroll);

    return () => {
      tableBody.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [allCases, loading, pagedData]); // Re-sync when page changes or data loads

  // Color mapping for tags based on the image
  const getCaseTypeColor = (type: string) => {
    const map: Record<string, { color: string, bg: string }> = {
      'Accommodation': { color: '#61C095', bg: '#61C0951A' },
      'Harassment': { color: '#BA6750', bg: '#BA67501A' },
      'Nonselection': { color: '#619CC0', bg: '#619CC01A' },
      'Different Treatment': { color: '#DFA069', bg: '#DFA0691A' },
      'Termination': { color: '#FB786E', bg: '#FB786E1A' },
      'Sexual Harassment': { color: '#EC4899', bg: '#FCE7F3' },
    };
    return map[type] || { color: '#6B7280', bg: '#F3F4F6' };
  };

  const getCaseBasisColor = (basis: string) => {
    const map: Record<string, { color: string, bg: string }> = {
      'Age': { color: '#FF74ED', bg: '#FF74ED1A' },
      'Color': { color: '#4A7F31', bg: '#4A7F311A' },
      'Mental/Physical Handicap': { color: '#74C061', bg: '#74C0611A' },
      'National Origin': { color: '#74C061', bg: '#74C0611A' },
      'Race': { color: '#7E6759', bg: '#7E67591A' },
      'Religion': { color: '#619CC0', bg: '#619CC01A' },
      'Reprisal': { color: '#8F5EFF', bg: '#8F5EFF1A' },
      'Sex': { color: '#8F5EFF', bg: '#8F5EFF1A' },
    };
    return map[basis] || { color: '#6B7280', bg: '#F3F4F6' };
  };

  const columns = [
    {
      title: 'Case Number',
      dataIndex: 'case_no',
      key: 'case_no',
      width: 140,
      render: (val: any) => <span style={{ fontWeight: 500, color: '#1F2A37' }}>{val}</span>
    },
    {
      title: 'Complaint',
      key: 'complainant',
      render: (record: CaseRecordProps) => (
        <span style={{ color: '#1F2A37' }}>{[record.first_name, record.middle, record.last_name].filter(Boolean).join(' ') || 'N/A'}</span>
      )
    },
    {
      title: 'Accused',
      key: 'accused',
      render: (record: CaseRecordProps) => (
        <span style={{ color: '#1F2A37' }}>{[record.accused_first_name, record.accused_middle, record.accused_last_name].filter(Boolean).join(' ') || 'N/A'}</span>
      )
    },
    {
      title: 'Case Basis',
      key: 'discrimination_basis',
      width: 180,
      render: (record: CaseRecordProps) => {
        const basis = record.discrimination_basis || record.basis || 'N/A';
        const colors = getCaseBasisColor(basis);
        return (
          <div style={{
            backgroundColor: colors.bg,
            color: colors.color,
            padding: '1px 9px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 400,
            display: 'inline-block',
            border: `1px solid ${colors.color}40`,
            textAlign: 'center',
            fontFamily: 'Lexend'
          }}>
            {basis}
          </div>
        );
      }
    },
    {
      title: 'Case Type',
      key: 'case_type',
      width: 180,
      render: (record: CaseRecordProps) => {
        const type = record.case_type || record.type || 'N/A';
        const colors = getCaseTypeColor(type);
        return (
          <div style={{
            backgroundColor: colors.bg,
            color: colors.color,
            padding: '1px 9px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 400,
            display: 'inline-block',
            border: `1px solid ${colors.color}40`,
            minWidth: '100px',
            textAlign: 'center',
            fontFamily: 'Lexend'
          }}>
            {type}
          </div>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: isAdmin ? 180 : 120,

      render: (record: CaseRecordProps) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeIcon style={{ fontSize: '20px', display: 'flex', alignItems: 'center' }} />}
            className="flex items-center justify-center gap-[5px] pl-[3px] pr-[9px] py-[3px] rounded-full font-normal text-[13px]"
            style={{ backgroundColor: '#4682B4', border: 'none', height: '26px' }}
            onClick={() => navigate(`/create-case-wizard?caseId=${record.id}&view=true`)}
          >
            View
          </Button>
          {isAdmin && (
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined style={{ fontSize: '14px' }} />}
              className="flex items-center justify-center gap-[5px] px-[8px] py-[3px] rounded-full font-normal text-[13px] border-none"
              style={{ height: '26px' }}
              onClick={(e) => {
                e.stopPropagation();
                setCaseToDelete(record);
                setDeleteModalVisible(true);
              }}
            >
              Delete
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="relative min-h-screen w-full flex flex-col bg-gradient-to-b from-[#a4c8e1] via-[#dceaf3] to-white font-['DM_Sans',sans-serif]">
      {/* Background Overlay */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover min-h-[220px] hidden md:block" />
        <img src={skyBgMobile} alt="" className="w-full h-auto object-cover md:hidden" />
      </div>

      {/* Header */}
      <Header currentUser={currentUser} showRefresh={true} onRefresh={fetchCases} />


      {/* Main Content */}
      <main className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12">
        {/* Search Card */}
        <div
          className="premium-card white w-full px-[18px] pt-[22px] pb-[31px] md:px-[24px] md:py-[28px] mb-[29px] md:mb-[34px]"
        >
          <div className="flex items-center gap-[11px] md:gap-[15px] mb-[23px] md:mb-[36px]">
            <FileText className="w-[18px] h-[21px] md:w-[22px] md:h-[25px]" />
            <h1 className="text-[14px] md:text-[20px] font-medium text-[#1F2A37] m-0 font-['Lexend']">View Interview</h1>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-[20px] md:gap-[24px] items-end">
            <div className="md:col-span-2 flex flex-col">
              <label className="text-[12px] md:text-[14px] font-medium text-[#1F2A37] mb-[11px] md:mb-[16px] font-['Lexend']">Search</label>
              <Input
                placeholder="Name, Company"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="h-[45px] rounded-[8px] bg-[#F3F4F6] border-none"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[12px] md:text-[14px] font-medium text-[#1F2A37] mb-[11px] md:mb-[16px] font-['Lexend']">Case Type</label>
              <Select
                className="w-full h-[45px] custom-select"
                value={selectedCaseType}
                onChange={setSelectedCaseType}
                placeholder="Select"
              >
                <Option value="all">Select</Option>
                <Option value="Accommodation">Accommodation</Option>
                <Option value="Different Treatment">Different Treatment</Option>
                <Option value="Harassment">Harassment</Option>
                <Option value="Nonselection">Nonselection</Option>
                <Option value="Sexual Harassment">Sexual Harassment</Option>
                <Option value="Termination">Termination</Option>
              </Select>
            </div>
            <div className="flex justify-between md:justify-start gap-2 md:gap-[24px] mt-[29px] md:mt-0">
              <Button
                className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-[#4682B4] hover:bg-[#3b6d96] text-white font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] border-none cursor-pointer transition-colors h-[32px] md:h-[47px]"
                onClick={fetchCases}
              >
                <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] rounded-full bg-white flex items-center justify-center text-[#4682B4]">
                  <div className="scale-[0.6] md:scale-100 flex items-center justify-center"><ArrowRight /></div>
                </div>
                Search
              </Button>
              <Button
                className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-white border border-[#4682B4] text-[#1F2A37] font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] cursor-pointer transition-colors hover:bg-[#f8FAFC] h-[32px] md:h-[47px]"
                onClick={() => { setSearchTerm(''); setSelectedCaseType('all'); }}
              >
                <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] flex items-center justify-center">
                  <div className="scale-[0.73] md:scale-100 flex items-center justify-center origin-center"><ArrowRightWhite /></div>
                </div>
                Clear
              </Button>
            </div>
          </div>
        </div>

        <div className="premium-card blue-tint w-full p-6 md:p-8 overflow-hidden" ref={tableContainerRef}>

          <style dangerouslySetInnerHTML={{
            __html: `
            .modern-table .ant-table-thead > tr > th {
              background: #4682B417 !important;
              color: #1F2A37 !important;
              font-weight: 400 !important;
              font-family: 'Lexend', sans-serif !important;
              border-bottom: none !important;
              padding: 16px !important;
              font-size: 10px !important;
            }
            @media (min-width: 768px) {
              .modern-table .ant-table-thead > tr > th {
                font-size: 14px !important;
              }
            }

            .modern-table .ant-table {
              background: transparent !important;
            }
            .modern-table .ant-table-container {
              background: transparent !important;
            }
            .modern-table .ant-table-row {
              transition: all 0.2s;
              background: transparent !important;
            }
            .modern-table .ant-table-row:hover {
              background-color: #F8FAFC !important;
            }
            .modern-table .ant-table-cell {
              border-bottom: 1px solid #F1F5F9 !important;
              padding: 16px !important;
              font-family: 'Lexend', sans-serif !important;
              background: transparent !important;
              font-size: 14px !important;
            }
            .custom-select .ant-select-selector {
              height: 45px !important;
              background-color: #F3F4F6 !important;
              border: none !important;
              border-radius: 8px !important;
              display: flex !important;
              align-items: center !important;
              padding: 0 16px !important;
            }
            .custom-select .ant-select-selection-item {
              line-height: 45px !important;
            }
            
            /* Hide Native Scrollbars completely */
            .modern-table .ant-table-content::-webkit-scrollbar,
            .modern-table .ant-table-body::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            .modern-table .ant-table-content {
               -ms-overflow-style: none !important;
               scrollbar-width: none !important;
            }

            .ant-table-pagination {
              display: none !important;
            }

            /* Profile Dropdown */
            .profile-dropdown .ant-dropdown-menu {
              background: linear-gradient(white, white) padding-box,
                          linear-gradient(to bottom right, #4682B4, transparent 24%, transparent 73%, #4682B4) border-box !important;
              border: 1px solid transparent !important;
              border-radius: 17px !important;
              box-shadow: 0px 8px 14px 3px rgba(0,0,0,0.08) !important;
              padding: 24px 20px !important;
            }
            .profile-dropdown .ant-dropdown-menu-item {
              padding: 0 0 !important;
              border-radius: 0 !important;
              background: transparent !important;
            }
            .profile-dropdown .ant-dropdown-menu-item:hover {
              background: transparent !important;
            }
            .profile-dropdown .ant-dropdown-menu-item-divider {
              margin: 8px 0 !important;
              background-color: #1F2A3712 !important;
            }
          `}} />
          <Table
            className="modern-table"
            columns={columns}
            dataSource={pagedData}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={false}
          />
        </div>

        {/* Custom Synchronized Scrollbar Outside the card */}
        {scrollInfo.width > scrollInfo.client && (
          <div className="w-full flex justify-center mt-[19px]">
            <div
              className="relative h-[4px] rounded-[3px] overflow-hidden"
              style={{
                width: '85.90%',
                backgroundColor: '#4682B430'
              }}
            >
              <div
                className="absolute h-full bg-[#4682B4] rounded-[3px] transition-all duration-100"
                style={{
                  width: `${(scrollInfo.client / scrollInfo.width) * 60}%`,
                  left: `${(scrollInfo.left / (scrollInfo.width - scrollInfo.client || 1)) * (100 - (scrollInfo.client / scrollInfo.width * 60))}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Separate Pagination outside the card */}
        <div className="w-full flex justify-end mt-[22px] md:mt-[42px]">
          <style dangerouslySetInnerHTML={{
            __html: `
            .custom-pagination-container {
              display: flex !important;
              align-items: center !important;
              justify-content: flex-end !important;
              width: 100% !important;
            }
            .custom-pagination-container .ant-pagination-total-text {
              order: 1 !important;
              color: #555D67 !important;
              font-family: 'Lexend', sans-serif !important;
              font-size: 10px !important;
              font-weight: 300 !important;
              margin-right: 8px !important;
            }
            @media (min-width: 768px) {
              .custom-pagination-container .ant-pagination-total-text {
                font-size: 14px !important;
                margin-right: 12px !important;
              }
            }
            /* Arrows Styling */
            .custom-pagination-container .ant-pagination-prev,
            .custom-pagination-container .ant-pagination-next {
              order: 2 !important;
              border: none !important;
              background: transparent !important;
              min-width: 20px !important;
              height: 32px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              color: #1F2A37 !important;
              font-size: 16px !important;
              margin: 0 !important;
            }
            /* Page Item & Jump (Dots) Styling */
            .custom-pagination-container .ant-pagination-item,
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              order: 2 !important;
              border-radius: 4px !important;
              height: 28px !important;
              min-width: 28px !important;
              line-height: 26px !important;
              background: white !important;
              font-family: 'Lexend', sans-serif !important;
              margin: 0 4px !important;
              font-size: 14px !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .custom-pagination-container .ant-pagination-item {
              border: 0.8px solid #4682B4 !important;
            }
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              border: none !important;
            }
            /* Hide non-active items on small screens */
            @media (max-width: 767px) {
              .custom-pagination-container .ant-pagination-item:not(.ant-pagination-item-active),
              .custom-pagination-container .ant-pagination-jump-prev,
              .custom-pagination-container .ant-pagination-jump-next {
                display: none !important;
              }
            }
            .custom-pagination-container .ant-pagination-item-active {
              border-color: #4682B4 !important;
            }
            .custom-pagination-container .ant-pagination-item-active a {
              color: #1F2A37 !important;
              font-weight: 500 !important;
            }
            /* Options/Page Changer Styling */
            .custom-pagination-container .ant-pagination-options {
              order: 3 !important;
              margin-left: 8px !important;
              display: block !important; /* Ensure it is shown on mobile */
            }
            .custom-pagination-container .ant-select-selector {
              background-color: #032B440D !important;
              border: none !important;
              border-radius: 4px !important;
              height: 28px !important;
              font-family: 'Lexend', sans-serif !important;
              font-size: 12px !important;
              font-weight: 300 !important;
              color: #1F2A37 !important;
              padding: 0 10px !important;
              display: flex !important;
              align-items: center !important;
            }
            .custom-pagination-container .ant-select-selection-item {
              line-height: 28px !important;
              color: #1F2A37 !important;
            }
            .custom-pagination-container .ant-select-arrow {
              color: #1F2A37 !important;
            }
          `}} />
          <Pagination
            className="custom-pagination-container"
            current={currentPage}
            pageSize={pageSize}
            total={filteredCases.length}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            showSizeChanger
            showTotal={() => `Total Interviews`}
            itemRender={(page, type, originalElement) => {
              if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
              if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
              return originalElement;
            }}
          />
        </div>
      </main>




      {/* Delete Modal */}
      <Modal
        title="Delete Interview"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: deleting }}
      >
        <p>Are you sure you want to delete Case #{caseToDelete?.case_no}?</p>
      </Modal>

      {/* Detail Modal (Keep for functionality) */}
      <Modal
        title={<div className="flex items-center gap-2"><InfoCircleOutlined className="text-[#4682B4]" /> Case Details - #{selectedCase?.case_no}</div>}
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        width="80%"
        footer={<Button type="primary" onClick={() => setIsDetailModalVisible(false)} style={{ backgroundColor: '#4682B4', border: 'none' }}>Close</Button>}
      >
        {selectedCase && (
          <Descriptions bordered column={2} className="mt-4">
            <Descriptions.Item label="Case #">{selectedCase.case_no}</Descriptions.Item>
            <Descriptions.Item label="Type">{selectedCase.case_type}</Descriptions.Item>
            <Descriptions.Item label="Complainant">{[selectedCase.first_name, selectedCase.last_name].join(' ')}</Descriptions.Item>
            <Descriptions.Item label="Accused">{[selectedCase.accused_first_name, selectedCase.accused_last_name].join(' ')}</Descriptions.Item>
            <Descriptions.Item label="Created At">{dayjs(selectedCase.created_at).format('MMMM D, YYYY')}</Descriptions.Item>
            <Descriptions.Item label="Company">{selectedCase.company_name || 'N/A'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      {/* Bottom Assets */}
      <div className="relative w-full pointer-events-none z-[2] flex justify-between items-end mt-auto">
        <img src={rocksLeft} alt="" className="w-[209px] md:w-[220px] lg:w-[250px] xl:w-[280px] h-auto object-contain object-left-bottom" />
        <img src={rocksRight} alt="" className="w-[110px] md:w-[150px] lg:w-[180px] xl:w-[210px] h-auto object-contain object-right-bottom" />
      </div>

      {/* Background Assets */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover md:min-h-[220px] hidden md:block" />
        <img src={skyBgMobile} alt="" className="w-full h-auto object-cover md:hidden" />
      </div>
    </div>

  );
};

export default CaseManagement;